import { useDebouncedCallback } from '@mantine/hooks'
import { AutoFixHigh, Visibility } from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormGroup,
  FormLabel,
  MenuItem,
  Stack,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AltGrowthButton from '~/components/altGrowthButton'
import CustomDialog from '~/components/customDialog'
import ActionErrorsDisplay from '~/features/campaigns/components/ofuManageCampaign/actionErrorsDisplay'
import useCampaignAssistantOptions from '~/features/campaigns/hooks/useCampaignAssistantOptions'
import useGetCampaignById from '~/features/campaigns/hooks/useGetCampaignById'
import useGetCampaignContactsInfinite from '~/features/campaigns/hooks/useGetCampaignContactsInfinite'
import {
  ECampaignBuilderFormKeys,
  ECampaignBuilderStepIds,
  ESaveAndTestForm,
  TCampaignGetContact,
} from '~/features/campaigns/models'
import {
  getMissingRequiredAgentMetadataForJunction,
  TGoLiveValidationErrorWithStep,
} from '~/features/campaigns/utils'
import { getCampaignJunctions } from '~/features/campaigns/utils/campaignJunctions'
import rem from '~/utils/ui'

export default function OFUSimulateContactInput({
  isDisabled,
  campaign: liveCampaign = null,
  handleStartSimulation,
  allowedAgentIds = undefined,
  variant = 'button',
  initialContact = undefined,
  buttonSx = {},
  onRegisterOpenHandler = undefined,
}: {
  isDisabled: boolean
  campaign?: Record<string, unknown> | null
  handleStartSimulation: (params: {
    contactData: Partial<TCampaignGetContact>
    agentId: string
  }) => void
  allowedAgentIds?: string[]
  variant?: 'button' | 'icon' | 'newUi' | 'hidden'
  initialContact?: Partial<TCampaignGetContact>
  buttonSx?: SxProps<Theme>
  onRegisterOpenHandler?: (openHandler: () => void) => void
}) {
  const theme = useTheme()
  const [simulationOpen, setSimulationOpen] = useState(false)
  const [debouncedContactSearch, setDebouncedContactSearch] = useState('')
  const [contactSearch, setContactSearch] = useState('')
  const [contactName, setContactName] = useState('John')
  const [selectedContact, setSelectedContact] =
    useState<TCampaignGetContact | null>(null)
  const [selectedSimulationAgentId, setSelectedSimulationAgentId] = useState('')
  const { id: campaignId } = useParams()
  const { data: { data: fetchedCampaign } = {} } = useGetCampaignById(
    campaignId || '',
  )
  const campaign = liveCampaign || fetchedCampaign || null
  const { assistantOptions, selectedAgentId, getDefaultAssistantId } =
    useCampaignAssistantOptions((campaign || null) as Record<string, unknown>)
  const assistantOptionsWithEligibility = useMemo(() => {
    const allowedSet = new Set((allowedAgentIds ?? []).map(String))
    const hasEligibilityFilter = allowedSet.size > 0
    return assistantOptions.map((option) => ({
      ...option,
      isEligible: hasEligibilityFilter
        ? allowedSet.has(String(option.id))
        : true,
    }))
  }, [allowedAgentIds, assistantOptions])
  const assistantValidationErrorsById = useMemo(() => {
    const byId = new Map<string, TGoLiveValidationErrorWithStep[]>()
    const junctions = getCampaignJunctions(
      (campaign || null) as Record<string, unknown>,
    )
    junctions.forEach((junction) => {
      const assistantId = String(junction.agentId || junction.Agent?.id || '')
      if (!assistantId) return
      const errors = getMissingRequiredAgentMetadataForJunction({
        campaign: (campaign || null) as Record<string, unknown>,
        junction,
      }).map((field) => ({
        message: `${assistantOptions.find((option) => option.id === assistantId)?.label || assistantId}: ${field.friendlyName || field.variable || 'Required field'}`,
        friendlyField: `${assistantOptions.find((option) => option.id === assistantId)?.label || assistantId}: ${field.friendlyName || field.variable || 'Required field'}`,
        stepId: ECampaignBuilderStepIds.multiAssistantSetup,
        fieldPath: `${ECampaignBuilderFormKeys.sharedCampaignAgentSettings}.${assistantId}.locationFields.${String(field.friendlyName || field.variable || '').trim()}`,
      }))
      byId.set(assistantId, errors)
    })
    return byId
  }, [assistantOptions, campaign])
  const eligibleAssistantOptions = assistantOptionsWithEligibility.filter(
    (option) => option.isEligible,
  )
  const shouldShowAssistantSelection = assistantOptions.length > 1

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setDebouncedContactSearch(value)
  }, 500)

  const { data: campaignContactsData, isLoading: isLoadingContacts } =
    useGetCampaignContactsInfinite({
      campaignId: campaignId || '',
      limit: 100,
      search: debouncedContactSearch,
      outreachFilter: 'all',
    })
  const simulationContacts = useMemo(
    () =>
      campaignContactsData?.pages?.flatMap(
        (page) => page.data?.campaignContacts || [],
      ) || [],
    [campaignContactsData],
  )
  const handleClear = () => {
    setContactName('John')
    setContactSearch('')
    setSelectedContact(null)
    setDebouncedContactSearch('')
  }

  const onStartSimulation = (contactData: Partial<TCampaignGetContact>) => {
    const agentId = selectedSimulationAgentId || selectedAgentId || ''
    handleStartSimulation({ contactData, agentId })
    setSimulationOpen(false)
    handleClear()
  }

  const handleClickStartSimulate = async () => {
    if (!campaignId) {
      toast.error('You need to create the campaign first')
      return
    }

    if (shouldShowAssistantSelection && eligibleAssistantOptions.length === 0) {
      toast.error(
        'No assistant is eligible for simulation. Complete required assistant details first.',
      )
      return
    }

    if (shouldShowAssistantSelection && !selectedSimulationAgentId) {
      toast.error('Please select an assistant to continue')
      return
    }

    if (selectedContact) {
      onStartSimulation(selectedContact)
      return
    }

    const contactData = {
      firstName: contactName,
      phone: '+1234567890',
      email: 'test@test.com',
      campaignId: campaignId || '',
      id: 'test-id',
    }

    onStartSimulation(contactData)
  }

  const handleCancel = () => {
    setSimulationOpen(false)
    handleClear()
  }

  const handleSearchContacts = (value: string) => {
    setContactSearch(value)
    debouncedSetSearch(value)
  }

  const handleValueChange = (value: TCampaignGetContact | null) => {
    setContactName('')
    if (!value) {
      setSelectedContact(null)
      return
    }

    setSelectedContact(value)
  }

  const handleButtonClick = useCallback(() => {
    if (initialContact) {
      handleStartSimulation({
        contactData: initialContact,
        agentId: selectedSimulationAgentId || selectedAgentId || '',
      })
      return
    }

    setSimulationOpen(true)
  }, [
    handleStartSimulation,
    initialContact,
    selectedAgentId,
    selectedSimulationAgentId,
  ])

  useEffect(() => {
    if (!onRegisterOpenHandler) {
      return
    }

    onRegisterOpenHandler(handleButtonClick)
  }, [handleButtonClick, onRegisterOpenHandler])

  useEffect(() => {
    if (!assistantOptionsWithEligibility.length) {
      if (selectedSimulationAgentId) {
        setSelectedSimulationAgentId('')
      }
      return
    }

    const isCurrentSelectionValid = assistantOptionsWithEligibility.some(
      (assistantOption) =>
        assistantOption.id === selectedSimulationAgentId &&
        assistantOption.isEligible,
    )
    if (isCurrentSelectionValid) {
      return
    }

    const defaultAgentId = getDefaultAssistantId(selectedAgentId)
    const defaultEligibleAgentId = assistantOptionsWithEligibility.find(
      (assistantOption) =>
        assistantOption.id === defaultAgentId && assistantOption.isEligible,
    )?.id
    const fallbackEligibleAgentId = eligibleAssistantOptions[0]?.id
    const nextSelectedAgentId =
      defaultEligibleAgentId || fallbackEligibleAgentId || ''

    if (nextSelectedAgentId !== selectedSimulationAgentId) {
      setSelectedSimulationAgentId(nextSelectedAgentId)
    }
  }, [
    assistantOptionsWithEligibility,
    eligibleAssistantOptions,
    getDefaultAssistantId,
    selectedAgentId,
    selectedSimulationAgentId,
  ])

  const renderButton = () => {
    if (variant === 'icon') {
      return (
        <Tooltip title="Simulate campaign using this contact.">
          <AltGrowthButton
            color="secondary"
            onClick={handleButtonClick}
            disabled={isDisabled}
            size="small"
            startIcon={<AutoFixHigh />}
            customSx={{
              width: rem(120),
            }}
          >
            Simulate
          </AltGrowthButton>
        </Tooltip>
      )
    }

    if (variant === 'newUi') {
      return (
        <Button
          variant="secondaryModern"
          startIcon={<Visibility />}
          onClick={handleButtonClick}
          sx={{
            height: rem(32),
            ...buttonSx,
          }}
          disabled={isDisabled}
        >
          Simulate
        </Button>
      )
    }

    if (variant === 'hidden') {
      return null
    }

    return (
      <AltGrowthButton
        size="big"
        color="primary"
        onClick={handleButtonClick}
        disabled={isDisabled}
        customSx={buttonSx}
      >
        Simulate campaign
      </AltGrowthButton>
    )
  }

  // If initialContact is provided, don't show the dialog button
  if (initialContact) {
    return renderButton()
  }

  return (
    <>
      {renderButton()}
      <CustomDialog
        isOpen={simulationOpen}
        onClose={handleCancel}
        onCancel={handleCancel}
        title="Simulate campaign"
        isDisabled={false}
        onConfirm={handleClickStartSimulate}
        cancelText="Cancel"
        confirmText="Simulate"
        maxWidth={500}
        description="Enter the details below and click Simulate to show what the ongoing campaign will look like."
      >
        <Stack
          sx={{
            width: '100%',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Alert
            severity="info"
            sx={{ boxSizing: 'border-box', width: '100%', mb: 2 }}
          >
            This is a simulation of the current campaign. No outreach will be
            sent to the contact.
          </Alert>
          <Stack
            sx={{
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            gap={3}
          >
            <FormGroup sx={{ gap: rem(8), width: '100%' }}>
              {shouldShowAssistantSelection && (
                <>
                  <FormLabel
                    sx={{
                      fontSize: rem(14),
                      color: theme.palette.mainFont,
                      fontWeight: 500,
                    }}
                  >
                    Assistant
                  </FormLabel>
                  <TextField
                    select
                    fullWidth
                    name="agentId"
                    value={selectedSimulationAgentId}
                    onChange={(event) => {
                      const nextAssistantId = String(event.target.value || '')
                      const isEligible = assistantOptionsWithEligibility.some(
                        (assistantOption) =>
                          assistantOption.id === nextAssistantId &&
                          assistantOption.isEligible,
                      )
                      if (!isEligible) {
                        return
                      }
                      setSelectedSimulationAgentId(nextAssistantId)
                    }}
                    size="small"
                    sx={{
                      '.MuiInputBase-root': {
                        height: rem(36),
                        borderRadius: rem(8),
                      },
                    }}
                  >
                    {assistantOptionsWithEligibility.map((assistantOption) => (
                      <MenuItem
                        key={assistantOption.id}
                        value={assistantOption.id}
                        onClick={(event) => {
                          if (!assistantOption.isEligible) {
                            event.preventDefault()
                          }
                        }}
                        sx={
                          !assistantOption.isEligible
                            ? {
                                color: theme.palette.text.disabled,
                              }
                            : undefined
                        }
                      >
                        {!assistantOption.isEligible ? (
                          <Tooltip
                            title={
                              <ActionErrorsDisplay
                                validationErrorsWithSteps={
                                  assistantValidationErrorsById.get(
                                    assistantOption.id,
                                  ) || []
                                }
                                campaignId={campaignId}
                                linkColor={theme.palette.common.white}
                                showLabel
                              />
                            }
                            placement="right"
                            leaveDelay={400}
                            disableInteractive={false}
                          >
                            <Box component="span" sx={{ width: '100%' }}>
                              {assistantOption.label}
                            </Box>
                          </Tooltip>
                        ) : (
                          assistantOption.label
                        )}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}

              <FormLabel
                sx={{
                  fontSize: rem(14),
                  color: theme.palette.mainFont,
                  fontWeight: 500,
                }}
              >
                Campaign contact
              </FormLabel>
              <Autocomplete
                fullWidth
                options={simulationContacts ?? []}
                getOptionLabel={(option) =>
                  `${`${option.firstName || ''} ${option.lastName || ''}`.trim() || 'Unnamed Contact'} ${option.email || option.phone ? `- ${option.email || option.phone}` : ''}`.trim()
                }
                loading={isLoadingContacts}
                onChange={(_, value) => handleValueChange(value)}
                onInputChange={(_, value) => {
                  handleSearchContacts(value)
                }}
                size="small"
                value={selectedContact}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      '.MuiInputBase-root': {
                        height: rem(36),
                        borderRadius: rem(8),
                      },
                    }}
                    placeholder="Select contact from campaign contacts"
                    value={contactSearch}
                  />
                )}
                sx={{
                  height: rem(36),
                }}
              />
            </FormGroup>

            <Typography variant="body2">
              Or enter new contact info below
            </Typography>

            <TextField
              fullWidth
              required
              name={ESaveAndTestForm.firstName}
              label="First Name"
              placeholder="Enter first name"
              value={contactName || selectedContact?.firstName || ''}
              onChange={(e) => setContactName(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              disabled={!!selectedContact}
              sx={{
                '.MuiInputBase-root': {
                  height: rem(36),
                  borderRadius: rem(8),
                },
              }}
            />
          </Stack>
        </Stack>
      </CustomDialog>
    </>
  )
}
